import { useEffect, useState } from 'react';
import SeoJsonLd from '../../components/feature/SeoJsonLd';

export default function HomePage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [openFaq, setOpenFaq] = useState<number>(0);

  // CEP states
  const [cep, setCep] = useState('');
  const [numero, setNumero] = useState('');
  const [complemento, setComplemento] = useState('');
  const [cepStep, setCepStep] = useState<'form' | 'success'>('form');
  const [cepData, setCepData] = useState<{ logradouro: string; bairro: string; localidade: string; uf: string } | null>(null);
  const [cepError, setCepError] = useState('');
  const [cepLoading, setCepLoading] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleFaq = (index: number) => {
    setOpenFaq(openFaq === index ? -1 : index);
  };

  const formatCep = (value: string) => {
    const digits = value.replace(/\D/g, '').slice(0, 8);
    if (digits.length > 5) return `${digits.slice(0, 5)}-${digits.slice(5)}`;
    return digits;
  };

  const handleCepChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatCep(e.target.value);
    setCep(formatted);
    setCepError('');
    if (formatted.replace(/\D/g, '').length === 8) {
      fetchCep(formatted.replace(/\D/g, ''));
    } else {
      setCepData(null);
    }
  };

  const fetchCep = async (digits: string) => {
    setCepLoading(true);
    setCepError('');
    try {
      const res = await fetch(`https://viacep.com.br/ws/${digits}/json/`);
      const data = await res.json();
      if (data.erro) {
        setCepError('CEP não encontrado. Verifique e tente novamente.');
        setCepData(null);
      } else {
        setCepData({ logradouro: data.logradouro, bairro: data.bairro, localidade: data.localidade, uf: data.uf });
      }
    } catch {
      setCepError('Erro ao buscar CEP. Tente novamente.');
      setCepData(null);
    } finally {
      setCepLoading(false);
    }
  };

  const handleCepSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!cepData) {
      setCepError('Por favor, insira um CEP válido.');
      return;
    }
    if (!numero.trim()) {
      setCepError('Por favor, informe o número.');
      return;
    }
    setCepStep('success');
  };

  const buildWhatsAppMessage = () => {
    if (!cepData) return '';
    const enderecoCompleto = `${cepData.logradouro}${numero ? ', ' + numero : ''}${complemento ? ' - ' + complemento : ''}, ${cepData.bairro}, ${cepData.localidade} - ${cepData.uf}, CEP: ${cep}`;
    const msg = `Olá! Vim pelo site da Central Cerâmica e quero fazer um pedido.\n\n📍 Endereço de entrega:\n${enderecoCompleto}\n\nGostaria de receber um orçamento!`;
    return encodeURIComponent(msg);
  };

  const handleReset = () => {
    setCep('');
    setNumero('');
    setComplemento('');
    setCepStep('form');
    setCepData(null);
    setCepError('');
  };

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://centralceramica.com.br';

  const localBusinessSchema = {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    name: 'Central Cerâmica',
    description: 'Blocos cerâmicos de vedação com entrega em 24h em todo estado de São Paulo. Qualidade certificada ABNT e melhor preço.',
    url: siteUrl,
    telephone: '+55-11-94303-8948',
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: '+55-11-94303-8948',
      contactType: 'sales',
      availableLanguage: 'Portuguese',
    },
    areaServed: {
      '@type': 'State',
      name: 'São Paulo',
      addressCountry: 'BR',
    },
    address: {
      '@type': 'PostalAddress',
      addressRegion: 'SP',
      addressCountry: 'BR',
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: -23.5505,
      longitude: -46.6333,
    },
    hasOfferCatalog: {
      '@type': 'OfferCatalog',
      name: 'Linha de Blocos Cerâmicos',
      itemListElement: [
        { '@type': 'Offer', itemOffered: { '@type': 'Product', name: 'Tijolo Especial Vedação 11,5x19x29cm' } },
        { '@type': 'Offer', itemOffered: { '@type': 'Product', name: 'Tijolo Blocão Vedação 14x19x29cm' } },
        { '@type': 'Offer', itemOffered: { '@type': 'Product', name: 'Tijolo Bloquinho Vedação 11,5x14x24cm' } },
        { '@type': 'Offer', itemOffered: { '@type': 'Product', name: 'Tijolo Baianinho Vedação 9x19x19cm' } },
        { '@type': 'Offer', itemOffered: { '@type': 'Product', name: 'Tijolo Linguiça Vedação 9x19x29cm' } },
        { '@type': 'Offer', itemOffered: { '@type': 'Product', name: 'Tijolo Canaletão Canaleta 14x19x29cm' } },
        { '@type': 'Offer', itemOffered: { '@type': 'Product', name: 'Tijolo Canaletinha Canaleta 11,5x14x24cm' } },
      ],
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: 'Os blocos de vocês seguem as normas técnicas?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Sim. Todos os nossos produtos seguem rigorosamente as normas da ABNT, com controle de qualidade em cada etapa da produção. Você recebe blocos padronizados, resistentes e prontos para garantir uma obra sólida e durável.',
        },
      },
      {
        '@type': 'Question',
        name: 'E se a entrega atrasar?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Comprou hoje, a carga completa chega até você em até 24h úteis, dentro do raio de atendimento.',
        },
      },
      {
        '@type': 'Question',
        name: 'O preço de vocês é competitivo mesmo?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Sim — e com vantagem. Pela qualidade, padronização e agilidade que entregamos, temos hoje o melhor custo-benefício da região. Em muitos casos, o barato sai caro com outras marcas que geram perdas e retrabalho.',
        },
      },
      {
        '@type': 'Question',
        name: 'Vocês vendem para qualquer obra? Ou só para depósitos?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Atendemos tanto distribuidores e depósitos quanto obras diretas, desde que o pedido seja de carga fechada. Construtores, empreiteiros e quem compra em volume têm prioridade no atendimento.',
        },
      },
      {
        '@type': 'Question',
        name: 'Vocês ajudam com o cálculo da quantidade de blocos?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Claro. Nossa equipe tira todas as dúvidas e ajuda você a calcular exatamente o que precisa, sem sobras e sem faltar. É só chamar no WhatsApp.',
        },
      },
      {
        '@type': 'Question',
        name: 'Vocês descarregam no local?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Sim. A entrega inclui descarga completa no local da obra ou do depósito, sem custo adicional. É mais praticidade, menos dor de cabeça.',
        },
      },
      {
        '@type': 'Question',
        name: 'Os blocos de vocês ajudam mesmo a economizar na obra?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Sim. A padronização e o encaixe perfeito reduzem retrabalho, desperdício e aceleram a execução. Isso pode gerar uma economia de até 20% em tempo e materiais.',
        },
      },
      {
        '@type': 'Question',
        name: 'Qual o raio de entrega?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Atendemos obras e depósitos em todo estado de São Paulo. Estamos prontos para expandir conforme a demanda.',
        },
      },
      {
        '@type': 'Question',
        name: 'Como faço pra pedir um orçamento?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Simples. Fale com nosso time direto no WhatsApp e receba seu orçamento personalizado em minutos.',
        },
      },
    ],
  };

  const itemListSchema = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: 'Produtos Central Cerâmica — Tijolos e Blocos Cerâmicos São Paulo',
    description: 'Linha completa de tijolos cerâmicos para vedação e canaleta com entrega em 24h em São Paulo.',
    numberOfItems: 7,
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Tijolo Especial Vedação 11,5x19x29cm', url: `${siteUrl}/` },
      { '@type': 'ListItem', position: 2, name: 'Tijolo Blocão Vedação 14x19x29cm', url: `${siteUrl}/` },
      { '@type': 'ListItem', position: 3, name: 'Tijolo Bloquinho Vedação 11,5x14x24cm', url: `${siteUrl}/` },
      { '@type': 'ListItem', position: 4, name: 'Tijolo Baianinho Vedação 9x19x19cm', url: `${siteUrl}/` },
      { '@type': 'ListItem', position: 5, name: 'Tijolo Linguiça Vedação 9x19x29cm', url: `${siteUrl}/` },
      { '@type': 'ListItem', position: 6, name: 'Tijolo Canaletão Canaleta 14x19x29cm', url: `${siteUrl}/` },
      { '@type': 'ListItem', position: 7, name: 'Tijolo Canaletinha Canaleta 11,5x14x24cm', url: `${siteUrl}/` },
    ],
  };

  return (
    <div className="min-h-screen bg-white">
      <SeoJsonLd data={localBusinessSchema} id="schema-local-business" />
      <SeoJsonLd data={faqSchema} id="schema-faq" />
      <SeoJsonLd data={itemListSchema} id="schema-item-list" />

      {/* Hero Section */}
      <section className="relative min-h-screen bg-cover bg-center" style={{ backgroundImage: 'url(https://lp.novafirenze.com.br/wp-content/uploads/2025/07/img1-nova-firenze.webp)' }}>
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
        
        {/* Navbar */}
        <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-md' : 'bg-transparent'}`}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <span className={`text-2xl font-bold tracking-tight ${isScrolled ? 'text-red-700' : 'text-white'}`}>
              Central Cerâmica
            </span>
          </div>
        </nav>

        {/* Hero Content */}
        <div className="relative z-10 flex items-center justify-center min-h-screen">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white w-full">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              <span className="font-bold">Receba seus tijolos amanhã.</span> Direto para você, do seu jeito e ainda pagando menos.
            </h1>
            <p className="text-lg sm:text-xl mb-8 max-w-3xl mx-auto">
              Blocos cerâmicos de vedação com entrega relâmpago, <strong>qualidade certificada e o melhor preço da região.</strong>
            </p>
            <p className="text-base sm:text-lg mb-8 text-white/90">
              Entrega em todo estado de São Paulo
            </p>
            <a 
              href="https://wa.me/5511943038948?text=Olá,%20vim%20através%20do%20site%20e%20gostaria%20de%20mais%20informações!" 
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block bg-red-700 hover:bg-red-800 text-white font-bold py-5 px-12 rounded-lg text-xl transition-all duration-300 transform hover:scale-95 whitespace-nowrap cursor-pointer shadow-lg"
            >
              Entre em contato
            </a>
          </div>
        </div>
      </section>

      {/* CEP Verification Section */}
      <section className="py-16 bg-gradient-to-br from-red-700 to-red-900 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'url(https://public.readdy.ai/ai/img_res/e1206d261829198ab818a9b917d6b788.jpg)', backgroundSize: 'cover' }}></div>
        <div className="relative z-10 max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="inline-flex items-center gap-2 bg-white/20 text-white rounded-full px-4 py-2 text-sm font-semibold mb-6">
              <i className="ri-map-pin-line text-lg"></i>
              Verifique a entrega na sua cidade
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-3">
              🚚 Entregamos na sua cidade?
            </h2>
            <p className="text-white/90 text-lg mb-8">
              Digite seu CEP e descubra se atendemos sua região — <strong>em menos de 24 horas!</strong>
            </p>

            <form onSubmit={handleCepSubmit} className="bg-white rounded-2xl p-8 shadow-2xl text-left">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                <div className="sm:col-span-2">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    CEP <span className="text-red-600">*</span>
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      value={cep}
                      onChange={handleCepChange}
                      placeholder="00000-000"
                      maxLength={9}
                      disabled={cepStep === 'success'}
                      className="w-full border-2 border-gray-200 rounded-lg px-4 py-3 text-lg font-semibold tracking-widest focus:outline-none focus:border-red-500 transition-colors disabled:bg-gray-50 disabled:border-green-400"
                    />
                    {cepLoading && (
                      <div className="absolute right-4 top-1/2 -translate-y-1/2">
                        <div className="w-5 h-5 border-2 border-red-500 border-t-transparent rounded-full animate-spin"></div>
                      </div>
                    )}
                    {cepData && !cepLoading && (
                      <div className="absolute right-4 top-1/2 -translate-y-1/2 text-green-500 text-xl">✓</div>
                    )}
                  </div>
                  {cepData && (
                    <p className="mt-2 text-sm text-gray-600 bg-gray-50 rounded-lg px-3 py-2">
                      📍 {cepData.logradouro && `${cepData.logradouro}, `}{cepData.bairro && `${cepData.bairro}, `}{cepData.localidade} - {cepData.uf}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Número <span className="text-red-600">*</span>
                  </label>
                  <input
                    type="text"
                    value={numero}
                    onChange={e => setNumero(e.target.value)}
                    placeholder="Ex: 123"
                    disabled={cepStep === 'success'}
                    className="w-full border-2 border-gray-200 rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-red-500 transition-colors disabled:bg-gray-50 disabled:border-green-400"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Complemento <span className="text-gray-400 font-normal">(opcional)</span>
                  </label>
                  <input
                    type="text"
                    value={complemento}
                    onChange={e => setComplemento(e.target.value)}
                    placeholder="Apto, Bloco, etc."
                    disabled={cepStep === 'success'}
                    className="w-full border-2 border-gray-200 rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-red-500 transition-colors disabled:bg-gray-50 disabled:border-green-400"
                  />
                </div>
              </div>

              {cepError && (
                <p className="text-red-600 text-sm mb-4 flex items-center gap-2">
                  <i className="ri-error-warning-line"></i> {cepError}
                </p>
              )}

              {cepStep === 'form' && (
                <button
                  type="submit"
                  disabled={!cepData || cepLoading}
                  className="w-full bg-red-700 hover:bg-red-800 disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-bold py-4 rounded-lg text-lg transition-all duration-300 transform hover:scale-95 whitespace-nowrap cursor-pointer shadow-md"
                >
                  Verificar disponibilidade de entrega →
                </button>
              )}

              {cepStep === 'success' && (
                <div className="mt-2">
                  {/* Success message */}
                  <div className="bg-green-50 border-2 border-green-400 rounded-xl px-6 py-5 mb-5 text-center">
                    <div className="flex items-center justify-center gap-3 mb-2">
                      <span className="text-3xl">✅</span>
                      <span className="text-2xl font-bold text-green-700">Ótima notícia! Entregamos aí!</span>
                    </div>
                    <div className="flex items-center justify-center gap-2 mt-3">
                      <span className="text-2xl">⚡</span>
                      <span className="text-xl font-bold text-green-700">Entrega em até 24 horas!</span>
                    </div>
                    <p className="text-green-700 text-base mt-2">
                      Faça seu pedido agora e receba amanhã. Entregamos e descarregamos na sua obra!
                    </p>
                  </div>

                  {/* WhatsApp button */}
                  <a
                    href={`https://wa.me/5511943038948?text=${buildWhatsAppMessage()}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-3 w-full bg-green-500 hover:bg-green-600 text-white font-bold py-5 rounded-xl text-xl transition-all duration-300 transform hover:scale-95 whitespace-nowrap cursor-pointer shadow-lg mb-4"
                    style={{ animation: 'whatsapp-pulse 2s infinite' }}
                  >
                    <i className="ri-whatsapp-line text-3xl"></i>
                    Pedir agora no WhatsApp
                  </a>

                  <button
                    type="button"
                    onClick={handleReset}
                    className="w-full text-gray-500 hover:text-gray-700 text-sm underline cursor-pointer transition-colors"
                  >
                    Verificar outro endereço
                  </button>
                </div>
              )}
            </form>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="hidden lg:block">
              <img 
                src="https://lp.novafirenze.com.br/wp-content/uploads/2025/07/Group-1171275343.png" 
                alt="Tijolos de qualidade" 
                className="w-full h-auto"
              />
            </div>
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold mb-6">
                ❌ Chega de <span className="font-bold">atraso, mofo e retrabalho</span>
              </h2>
              <div className="lg:hidden mb-6">
                <img 
                  src="https://lp.novafirenze.com.br/wp-content/uploads/2025/07/Group-1171275343.png" 
                  alt="Tijolos de qualidade" 
                  className="w-full h-auto"
                />
              </div>
              <p className="text-lg mb-6 text-gray-700">
                Se você tem um depósito ou cuida de uma obra e já teve prejuízo por culpa de entrega atrasada, bloco fora da medida ou material de baixa qualidade, sabe o quanto isso custa.
              </p>
              <p className="text-lg mb-6 text-gray-900 font-semibold">
                A Central Cerâmica resolve isso
              </p>
              <ul className="space-y-4">
                <li className="flex items-start">
                  <span className="text-green-600 mr-3 text-xl">✓</span>
                  <span className="text-gray-700">Entregamos carga completa em até 1 dia útil.</span>
                </li>
                <li className="flex items-start">
                  <span className="text-green-600 mr-3 text-xl">✓</span>
                  <span className="text-gray-700">Blocos bonitos, na medida e fáceis de trabalhar.</span>
                </li>
                <li className="flex items-start">
                  <span className="text-green-600 mr-3 text-xl">✓</span>
                  <span className="text-gray-700">Atendimento rápido e confiável do início ao fim.</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Product Intro */}
      <section className="py-16 bg-red-700">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
          <h2 className="text-3xl sm:text-4xl font-bold mb-4">
            O bloco certo, na medida certa, no tempo que você precisa.
          </h2>
          <p className="text-lg">
            Ideais para vedação e canaleta, prontos para chegar amanhã, se for carga completa — sem erro de medida nem atraso e com a máxima qualidade.
          </p>
        </div>
      </section>

      {/* Products Grid */}
      <section className="py-16 bg-gray-50" data-product-shop>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Product 1 */}
            <div className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-xl transition-shadow duration-300">
              <div className="w-full h-64 bg-gray-100">
                <img 
                  src="https://lp.novafirenze.com.br/wp-content/uploads/2025/07/Frame-1707478543.webp" 
                  alt="Tijolo Especial" 
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="p-6">
                <h3 className="text-xl font-bold mb-2">🔍 TIJOLO ESPECIAL | VEDAÇÃO</h3>
                <div className="border-t border-gray-200 my-4"></div>
                <p className="text-gray-600 mb-3">(11,5 x 19 x 29 cm)</p>
                <p className="text-gray-700 mb-4">
                  Para quem precisa de um bloco com equilíbrio entre leveza, resistência e eficiência na execução
                </p>
                <p className="text-gray-700">
                  🧱 Ideal para obras que exigem mais controle dimensional e acabamento refinado.
                </p>
              </div>
            </div>

            {/* Product 2 */}
            <div className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-xl transition-shadow duration-300">
              <div className="w-full h-64 bg-gray-100">
                <img 
                  src="https://lp.novafirenze.com.br/wp-content/uploads/2025/07/2224b41ba47f0b5230414fa17aa4073f.png" 
                  alt="Tijolo Blocão" 
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="p-6">
                <h3 className="text-xl font-bold mb-2">🔍 TIJOLO BLOCÃO | VEDAÇÃO</h3>
                <div className="border-t border-gray-200 my-4"></div>
                <p className="text-gray-600 mb-3">(14 x 19 x 29 cm)</p>
                <p className="text-gray-700 mb-4">
                  Para quem precisa de resistência, agilidade na montagem e menor consumo de argamassa.
                </p>
                <p className="text-gray-700">
                  🧱 Bloco robusto e preciso. Ideal para paredes com desempenho térmico e acústico superior.
                </p>
              </div>
            </div>

            {/* Product 3 */}
            <div className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-xl transition-shadow duration-300">
              <div className="w-full h-64 bg-gray-100">
                <img 
                  src="https://lp.novafirenze.com.br/wp-content/uploads/2025/07/5c44c181b2878e7f05b752668918fba2.png" 
                  alt="Tijolo Bloquinho" 
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="p-6">
                <h3 className="text-xl font-bold mb-2">🔍 TIJOLO BLOQUINHO | VEDAÇÃO</h3>
                <div className="border-t border-gray-200 my-4"></div>
                <p className="text-gray-600 mb-3">(11,5 x 14 x 24 cm)</p>
                <p className="text-gray-700 mb-4">
                  O queridinho dos construtores. Compacto, preciso e ideal para obras com alto padrão de acabamento.
                </p>
                <p className="text-gray-700">
                  🧱 Medida certeira. Zero desperdício. Excelente encaixe.
                </p>
              </div>
            </div>

            {/* Product 4 */}
            <div className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-xl transition-shadow duration-300">
              <div className="w-full h-64 bg-gray-100">
                <img 
                  src="https://lp.novafirenze.com.br/wp-content/uploads/2025/07/9057dee4ee5c504c069eff597c91a651.png" 
                  alt="Tijolo Baianinho" 
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="p-6">
                <h3 className="text-xl font-bold mb-2">🔍 TIJOLO BAIANINHO | VEDAÇÃO</h3>
                <div className="border-t border-gray-200 my-4"></div>
                <p className="text-gray-600 mb-3">(9 x 19 x 19 cm)</p>
                <p className="text-gray-700 mb-4">
                  Perfeito para quem quer versatilidade e economia em áreas menores ou divisórias.
                </p>
                <p className="text-gray-700">
                  🧱 Leve, fácil de trabalhar e com excelente custo-benefício.
                </p>
              </div>
            </div>

            {/* Product 5 */}
            <div className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-xl transition-shadow duration-300">
              <div className="w-full h-64 bg-gray-100">
                <img 
                  src="https://lp.novafirenze.com.br/wp-content/uploads/2025/07/5b454b2cba08bf391d2598675777cd1b.png" 
                  alt="Tijolo Linguiça" 
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="p-6">
                <h3 className="text-xl font-bold mb-2">🔍 TIJOLO LINGUIÇA | VEDAÇÃO</h3>
                <div className="border-t border-gray-200 my-4"></div>
                <p className="text-gray-600 mb-3">(9 x 19 x 29 cm)</p>
                <p className="text-gray-700 mb-4">
                  Mais leve, mais rápido de assentar e com encaixe eficiente.
                </p>
                <p className="text-gray-700">
                  🧱 Opção ideal para paredes com menor carga e execução mais ágil.
                </p>
              </div>
            </div>

            {/* Product 6 */}
            <div className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-xl transition-shadow duration-300">
              <div className="w-full h-64 bg-gray-100">
                <img 
                  src="https://lp.novafirenze.com.br/wp-content/uploads/2025/07/tijolo-canaleta.webp" 
                  alt="Tijolo Canaletão" 
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="p-6">
                <h3 className="text-xl font-bold mb-2">🔍 TIJOLO CANALETÃO | CANALETA</h3>
                <div className="border-t border-gray-200 my-4"></div>
                <p className="text-gray-600 mb-3">(14 x 19 x 29 cm)</p>
                <p className="text-gray-700 mb-4">
                  Feito para reforço estrutural com canaletas limpas, alinhadas e fáceis de concretar.
                </p>
                <p className="text-gray-700">
                  🧱 Reforço técnico com padrão visual impecável.
                </p>
              </div>
            </div>

            {/* Product 7 */}
            <div className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-xl transition-shadow duration-300">
              <div className="w-full h-64 bg-gray-100">
                <img 
                  src="https://lp.novafirenze.com.br/wp-content/uploads/2025/07/tijolo-canaletinha.webp" 
                  alt="Tijolo Canaletinha" 
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="p-6">
                <h3 className="text-xl font-bold mb-2">🔍 TIJOLO CANALETINHA | CANALETA</h3>
                <div className="border-t border-gray-200 my-4"></div>
                <p className="text-gray-600 mb-3">(11,5 x 14 x 24 cm)</p>
                <p className="text-gray-700 mb-4">
                  Solução compacta e funcional para vigas e vergas.
                </p>
                <p className="text-gray-700">
                  🧱 Canaleta na medida certa para reforço técnico em áreas mais enxutas.
                </p>
              </div>
            </div>
          </div>

          <div className="text-center mt-12">
            <a 
              href="https://wa.me/5511943038948?text=Olá,%20vim%20através%20do%20site%20e%20gostaria%20de%20mais%20informações!" 
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block bg-red-700 hover:bg-red-800 text-white font-bold py-5 px-12 rounded-lg text-xl transition-all duration-300 transform hover:scale-95 whitespace-nowrap cursor-pointer shadow-lg"
            >
              Entre em contato
            </a>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div className="space-y-8">
              <div className="bg-red-700 text-white p-8 rounded-lg">
                <div className="flex items-start mb-4">
                  <div className="text-4xl mr-4">✓</div>
                  <div>
                    <h3 className="text-2xl font-bold mb-2">Menor custo</h3>
                    <p>Reduza o custo da obra com menos desperdício, menos retrabalho e mais produtividade.</p>
                  </div>
                </div>
              </div>

              <div className="bg-red-700 text-white p-8 rounded-lg">
                <div className="flex items-start mb-4">
                  <div className="text-4xl mr-4">✓</div>
                  <div>
                    <h3 className="text-2xl font-bold mb-2">Maior qualidade</h3>
                    <p>Isolamento térmico e acústico superior, que valoriza a obra e melhora a experiência de quem vai morar.</p>
                  </div>
                </div>
              </div>

              <div className="bg-red-700 text-white p-8 rounded-lg">
                <div className="flex items-start mb-4">
                  <div className="text-4xl mr-4">✓</div>
                  <div>
                    <h3 className="text-2xl font-bold mb-2">Menos problemas</h3>
                    <p>Baixa absorção de água = <span className="font-bold">menos infiltração, menos mofo, mais durabilidade.</span></p>
                  </div>
                </div>
              </div>

              <div className="bg-red-700 text-white p-8 rounded-lg">
                <div className="flex items-start mb-4">
                  <div className="text-4xl mr-4">✓</div>
                  <div>
                    <h3 className="text-2xl font-bold mb-2">Mais sustentável</h3>
                    <p>Produção com <span className="font-bold">energia limpa (biomassa)</span> e redução de até <span className="font-bold">66% nas emissões de CO₂.</span></p>
                  </div>
                </div>
              </div>
            </div>

            <div className="hidden lg:block">
              <img 
                src="https://lp.novafirenze.com.br/wp-content/uploads/2025/07/Group-1171275369.webp" 
                alt="Caminhão de entrega" 
                className="w-full h-auto rounded-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center mb-12">
            Essa vez será <span className="text-red-700">diferente.</span>
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            <div className="bg-white p-8 rounded-lg shadow-md">
              <div className="text-4xl mb-4">🏆</div>
              <h3 className="text-2xl font-bold mb-4">
                Qualidade que dá <span className="text-red-700">segurança</span>
              </h3>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start">
                  <span className="text-green-600 mr-2">✓</span>
                  <span>Dentro da norma ABNT</span>
                </li>
                <li className="flex items-start">
                  <span className="text-green-600 mr-2">✓</span>
                  <span>Acabamento limpo e preciso</span>
                </li>
                <li className="flex items-start">
                  <span className="text-green-600 mr-2">✓</span>
                  <span>Baixa absorção e alta durabilidade</span>
                </li>
              </ul>
            </div>

            <div className="bg-white p-8 rounded-lg shadow-md">
              <div className="text-4xl mb-4">⚡</div>
              <h3 className="text-2xl font-bold mb-4">
                Bloco que ajuda a <span className="text-red-700">acelerar</span>
              </h3>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start">
                  <span className="text-green-600 mr-2">✓</span>
                  <span>Medidas exatas</span>
                </li>
                <li className="flex items-start">
                  <span className="text-green-600 mr-2">✓</span>
                  <span>Encaixe fácil</span>
                </li>
                <li className="flex items-start">
                  <span className="text-green-600 mr-2">✓</span>
                  <span>Obra mais rápida, mais limpa</span>
                </li>
              </ul>
            </div>

            <div className="bg-white p-8 rounded-lg shadow-md">
              <div className="text-4xl mb-4">💬</div>
              <h3 className="text-2xl font-bold mb-4">
                Atendimento que <span className="text-red-700">resolve</span>
              </h3>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start">
                  <span className="text-green-600 mr-2">✓</span>
                  <span>Suporte técnico e cálculo de consumo</span>
                </li>
                <li className="flex items-start">
                  <span className="text-green-600 mr-2">✓</span>
                  <span>Resposta rápida no WhatsApp</span>
                </li>
                <li className="flex items-start">
                  <span className="text-green-600 mr-2">✓</span>
                  <span>Venda consultiva, olho no olho</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-lg shadow-md">
              <div className="text-4xl mb-4">🚚</div>
              <h3 className="text-2xl font-bold mb-4">
                Entrega em 24h<br />
                <span className="text-red-700">(carga completa)</span>
              </h3>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start">
                  <span className="text-green-600 mr-2">✓</span>
                  <span>Logística própria</span>
                </li>
                <li className="flex items-start">
                  <span className="text-green-600 mr-2">✓</span>
                  <span>Entregamos e descarregamos na obra</span>
                </li>
                <li className="flex items-start">
                  <span className="text-green-600 mr-2">✓</span>
                  <span>Sem atraso, sem desculpa</span>
                </li>
              </ul>
            </div>

            <div className="bg-white p-8 rounded-lg shadow-md">
              <div className="text-4xl mb-4">📍</div>
              <h3 className="text-2xl font-bold mb-4">
                Atendemos <span className="text-red-700">todo estado de São Paulo</span>
              </h3>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start">
                  <span className="text-green-600 mr-2">✓</span>
                  <span>Região sempre abastecida</span>
                </li>
                <li className="flex items-start">
                  <span className="text-green-600 mr-2">✓</span>
                  <span>Clientes fiéis em diversas cidades</span>
                </li>
                <li className="flex items-start">
                  <span className="text-green-600 mr-2">✓</span>
                  <span>Presença forte no estado de SP</span>
                </li>
              </ul>
            </div>

            <div className="bg-white p-8 rounded-lg shadow-md">
              <div className="text-4xl mb-4">💰</div>
              <h3 className="text-2xl font-bold mb-4">
                Economia <span className="text-red-700">real</span> na obra
              </h3>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start">
                  <span className="text-green-600 mr-2">✓</span>
                  <span>Menos perda, menos retrabalho</span>
                </li>
                <li className="flex items-start">
                  <span className="text-green-600 mr-2">✓</span>
                  <span>Até 20% de redução de custo</span>
                </li>
                <li className="flex items-start">
                  <span className="text-green-600 mr-2">✓</span>
                  <span>Bloco bom custa menos no fim</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-red-700 font-bold text-lg mb-4">| Quem compra, <span className="font-bold">recomenda.</span></p>
            <h2 className="text-3xl font-bold">
              Veja o que nossos clientes estão falando
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-gray-50 p-6 rounded-lg shadow-md">
              <img 
                src="https://lp.novafirenze.com.br/wp-content/uploads/2025/07/dep1-3.webp" 
                alt="Depoimento 1" 
                className="w-full h-auto rounded-lg"
              />
            </div>
            <div className="bg-gray-50 p-6 rounded-lg shadow-md">
              <img 
                src="https://lp.novafirenze.com.br/wp-content/uploads/2025/07/dep2-2.webp" 
                alt="Depoimento 2" 
                className="w-full h-auto rounded-lg"
              />
            </div>
            <div className="bg-gray-50 p-6 rounded-lg shadow-md">
              <img 
                src="https://lp.novafirenze.com.br/wp-content/uploads/2025/07/dep3-2.webp" 
                alt="Depoimento 3" 
                className="w-full h-auto rounded-lg"
              />
            </div>
            <div className="bg-gray-50 p-6 rounded-lg shadow-md">
              <img 
                src="https://lp.novafirenze.com.br/wp-content/uploads/2025/07/dep4-1.webp" 
                alt="Depoimento 4" 
                className="w-full h-auto rounded-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Comparison Table */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-bold text-center mb-12">
            <span className="text-red-700">Compare e comprove:</span> o que muda quando você escolhe a Central Cerâmica.
          </h2>

          <div className="bg-white rounded-lg shadow-lg overflow-hidden">
            <div className="grid grid-cols-3 gap-0">
              {/* Header */}
              <div className="bg-gray-100 p-4 border-b border-r border-gray-200"></div>
              <div className="bg-red-700 text-white p-4 border-b border-r border-gray-200 font-bold text-center">
                Central Cerâmica
              </div>
              <div className="bg-gray-200 p-4 border-b border-gray-200 font-bold text-center">
                Comum no mercado
              </div>

              {/* Row 1 */}
              <div className="bg-gray-50 p-4 border-b border-r border-gray-200 font-semibold">
                Qualidade dos blocos
              </div>
              <div className="p-4 border-b border-r border-gray-200 text-sm">
                Dentro das normas técnicas, visual impecável, encaixe preciso
              </div>
              <div className="p-4 border-b border-gray-200 text-sm">
                Varia bastante entre lotes e marcas
              </div>

              {/* Row 2 */}
              <div className="bg-gray-50 p-4 border-b border-r border-gray-200 font-semibold">
                Tempo de entrega
              </div>
              <div className="p-4 border-b border-r border-gray-200 text-sm">
                Entrega + descarregamento em <strong>até 24h úteis</strong>
              </div>
              <div className="p-4 border-b border-gray-200 text-sm">
                Pode levar dias (ou até semanas)
              </div>

              {/* Row 3 */}
              <div className="bg-gray-50 p-4 border-b border-r border-gray-200 font-semibold">
                Atendimento
              </div>
              <div className="p-4 border-b border-r border-gray-200 text-sm">
                Humanizado, técnico e direto no WhatsApp
              </div>
              <div className="p-4 border-b border-gray-200 text-sm">
                Atendimento demorado ou impessoal
              </div>

              {/* Row 4 */}
              <div className="bg-gray-50 p-4 border-b border-r border-gray-200 font-semibold">
                Padronização dimensional
              </div>
              <div className="p-4 border-b border-r border-gray-200 text-sm">
                Alta precisão → menos cortes, mais economia
              </div>
              <div className="p-4 border-b border-gray-200 text-sm">
                Pode gerar retrabalho na obra
              </div>

              {/* Row 5 */}
              <div className="bg-gray-50 p-4 border-b border-r border-gray-200 font-semibold">
                Sustentabilidade
              </div>
              <div className="p-4 border-b border-r border-gray-200 text-sm">
                Queima com biomassa, emissão de CO₂ reduzida
              </div>
              <div className="p-4 border-b border-gray-200 text-sm">
                Pouca ou nenhuma preocupação ambiental
              </div>

              {/* Row 6 */}
              <div className="bg-gray-50 p-4 border-b border-r border-gray-200 font-semibold">
                Preço
              </div>
              <div className="p-4 border-b border-r border-gray-200 text-sm">
                Melhor custo-benefício pela <strong>qualidade entregue</strong>
              </div>
              <div className="p-4 border-b border-gray-200 text-sm">
                Às vezes mais barato, mas com mais perdas
              </div>

              {/* Row 7 */}
              <div className="bg-gray-50 p-4 border-r border-gray-200 font-semibold">
                Variedade de modelos
              </div>
              <div className="p-4 border-r border-gray-200 text-sm">
                Linha completa para vedação e canaleta
              </div>
              <div className="p-4 text-sm">
                Algumas oferecem poucos formatos
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 bg-red-700">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
          <h2 className="text-3xl sm:text-4xl font-bold mb-4">
            Garanta o <strong>melhor bloco</strong> para você.
          </h2>
          <p className="text-xl mb-8">Faça agora sua cotação.</p>
          <a 
            href="https://wa.me/5511943038948?text=Olá,%20vim%20através%20do%20site%20e%20gostaria%20de%20mais%20informações!" 
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block bg-white text-red-700 hover:bg-gray-100 font-bold py-5 px-12 rounded-lg text-xl transition-all duration-300 transform hover:scale-95 whitespace-nowrap cursor-pointer shadow-lg"
          >
            FALE CONOSCO AGORA
          </a>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center mb-12">
            Talvez você esteja pensando…
          </h2>

          <div className="space-y-4">
            {[
              {
                q: "1. Os blocos de vocês seguem as normas técnicas?",
                a: "Sim. Todos os nossos produtos seguem rigorosamente as normas da ABNT, com controle de qualidade em cada etapa da produção. Você recebe blocos padronizados, resistentes e prontos para garantir uma obra sólida e durável."
              },
              {
                q: "2. E se a entrega atrasar?",
                a: "Comprou hoje, a carga completa chega até você em até 24h úteis, dentro do raio de atendimento."
              },
              {
                q: "3. O preço de vocês é competitivo mesmo?",
                a: "Sim — e com vantagem. Pela qualidade, padronização e agilidade que entregamos, temos hoje o melhor custo-benefício da região. Em muitos casos, o barato sai caro com outras marcas que geram perdas e retrabalho."
              },
              {
                q: "4. Vocês vendem para qualquer obra? Ou só para depósitos?",
                a: "Atendemos tanto distribuidores e depósitos quanto obras diretas, desde que o pedido seja de carga fechada. Construtores, empreiteiros e quem compra em volume têm prioridade no atendimento."
              },
              {
                q: "5. Vocês ajudam com o cálculo da quantidade de blocos?",
                a: "Claro. Nossa equipe tira todas as dúvidas e ajuda você a calcular exatamente o que precisa, sem sobras e sem faltar. É só chamar no WhatsApp."
              },
              {
                q: "6. Vocês descarregam no local?",
                a: "Sim. A entrega inclui descarga completa no local da obra ou do depósito, sem custo adicional. É mais praticidade, menos dor de cabeça."
              },
              {
                q: "7. Os blocos de vocês ajudam mesmo a economizar na obra?",
                a: "Sim. A padronização e o encaixe perfeito reduzem retrabalho, desperdício e aceleram a execução. Isso pode gerar uma economia de até 20% em tempo e materiais."
              },
              {
                q: "8. E o acabamento? Os blocos são bonitos?",
                a: "Muito. Nossos blocos têm acabamento limpo, cor uniforme e ótimo aspecto visual — o que ajuda até na estética final da obra. Quem conhece, reconhece a diferença."
              },
              {
                q: "9. Qual o raio de entrega?",
                a: "Atendemos obras e depósitos em todo estado de São Paulo. Estamos prontos para expandir conforme a demanda."
              },
              {
                q: "10. Como faço pra pedir um orçamento?",
                a: "Simples. Clique no botão abaixo, fale com nosso time direto no WhatsApp e receba seu orçamento personalizado em minutos."
              }
            ].map((faq, index) => (
              <div key={index} className="border border-gray-200 rounded-lg overflow-hidden">
                <button
                  onClick={() => toggleFaq(index)}
                  className="w-full flex items-center justify-between p-6 text-left bg-white hover:bg-gray-50 transition-colors duration-200 cursor-pointer"
                >
                  <span className="font-semibold text-gray-900">{faq.q}</span>
                  <span className="text-2xl text-red-700">
                    {openFaq === index ? '−' : '+'}
                  </span>
                </button>
                {openFaq === index && (
                  <div className="p-6 bg-gray-50 border-t border-gray-200">
                    <p className="text-gray-700">{faq.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <a 
              href="https://wa.me/5511943038948?text=Olá,%20vim%20através%20do%20site%20e%20gostaria%20de%20mais%20informações!" 
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block bg-red-700 hover:bg-red-800 text-white font-bold py-5 px-12 rounded-lg text-xl transition-all duration-300 transform hover:scale-95 whitespace-nowrap cursor-pointer shadow-lg"
            >
              Entre em contato
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-800 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <span className="text-2xl font-bold tracking-tight text-white">Central Cerâmica</span>
          </div>
          
          <div className="text-center text-sm text-gray-400 space-y-4">
            <p>
              Ao clicar no botão você concorda que os dados pessoais fornecidos acima serão utilizados para envio de conteúdo informativo, analítico e publicitário sobre produtos, serviços e assuntos gerais, nos termos da Lei Geral de Proteção de Dados.
            </p>
            <p className="font-semibold">
              © 2025 COPYRIGHT - CENTRAL CERÂMICA - TODOS OS DIREITOS RESERVADOS.
            </p>
          </div>
        </div>
      </footer>

      {/* WhatsApp Floating Button */}
      <a
        href="https://wa.me/5511943038948?text=Olá,%20vim%20através%20do%20site%20e%20gostaria%20de%20mais%20informações!"
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 right-6 z-50 flex items-center gap-3 bg-green-500 hover:bg-green-600 text-white rounded-full shadow-2xl transition-all duration-300 transform hover:scale-105 cursor-pointer px-5 py-4"
        aria-label="WhatsApp"
        style={{ animation: 'whatsapp-pulse 2s infinite' }}
      >
        <i className="ri-whatsapp-line text-4xl"></i>
        <span className="font-bold text-lg whitespace-nowrap">Fale agora no WhatsApp</span>
        <style>{`
          @keyframes whatsapp-pulse {
            0% { box-shadow: 0 0 0 0 rgba(34,197,94,0.7); }
            70% { box-shadow: 0 0 0 18px rgba(34,197,94,0); }
            100% { box-shadow: 0 0 0 0 rgba(34,197,94,0); }
          }
        `}</style>
      </a>
    </div>
  );
}